import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { FORGE_PARTS, type PartName } from './partNames';

// Kitbash part library — Phase 2 of docs/SHIP-RENDERING-ARCHITECTURE.md.
// Parts are geometry-only GLBs forged by tools/parts/build_parts.py
// (headless Blender). The contract:
//   - authored along +Y (three space), unit nominal size, origin = attach point
//   - placeholder materials named by slot — swapped here for the ship's
//     triplanar MatSet via PART_SLOTS below (glow -> the emissive port
//     material); an unlisted name binds slate
//   - geometry is SHARED across every clone in the fleet; meshes are marked
//     userData.sharedGeometry so per-ship dispose() leaves it alive

export { FORGE_PARTS, HULL_PARTS, PART_NAMES, type PartName } from './partNames';

/** The material slots a part can bind — structurally satisfied by custos MatSet.
 *  The optional ones are an authored hull's own paint; a ship that lacks one
 *  binds slate there, the same as an unlisted name. */
export interface PartMats {
  hull: THREE.Material;
  slate: THREE.Material;
  armor: THREE.Material;
  port: THREE.Material;
  rad: THREE.Material;
  bandA?: THREE.Material;
  bandB?: THREE.Material;
  tile?: THREE.Material;
  cargo?: THREE.Material;
  bio?: THREE.Material;
  driveGlow?: THREE.Material;
  driveHeat?: THREE.Material;
  glass?: THREE.Material;
}

/**
 * Placeholder material name → the MatSet slot it binds. Anything unlisted
 * falls through to `slate`, which is why a missing entry is SILENT: the part
 * still draws, just in the wrong material.
 *
 * Exported because that fallthrough is only provable by taking an entry out —
 * tools/preview/radiator-rig-check.mjs drops the two rad slots and asserts the
 * material reading goes red while the geometry reading stays green.
 */
export const PART_SLOTS: Record<string, keyof PartMats> = {
  glow: 'port', hull: 'hull', armor: 'armor', slate: 'slate',
  // rad-white is the coated radiating face and its flow tubes, rad-fin the
  // closeouts and stiffener. Both take the rig's glowing material: miss them
  // and the panels bind to slate while the radiator glow (radiatorGlow.ts)
  // goes on heating a material nothing in the scene is drawing.
  'rad-white': 'rad', 'rad-fin': 'rad',
  // An authored hull's livery and its tells. tools/parts/ingest_hull.py reads
  // these keys and passes a material already named one of them straight through.
  'band-a': 'bandA', 'band-b': 'bandB', tile: 'tile', cargo: 'cargo', bio: 'bio',
  'drive-glow': 'driveGlow', 'drive-heat': 'driveHeat', glass: 'glass',
};

const _loader = new GLTFLoader();
const _cache = new Map<string, Promise<THREE.Group>>();

function loadPart(name: PartName): Promise<THREE.Group> {
  let p = _cache.get(name);
  if (!p) {
    p = _loader.loadAsync(`/art/parts/${name}.glb`).then((gltf) => {
      gltf.scene.traverse((o) => {
        if ((o as THREE.Mesh).isMesh) o.userData.sharedGeometry = true;
      });
      return gltf.scene;
    });
    _cache.set(name, p);
  }
  return p;
}

/** Warm the FORGE part cache only (PoC awaits this for deterministic headless
 * shots). Multi-MB hull GLBs are deliberately excluded — lazy is kind. */
export function preloadParts(): Promise<unknown> {
  return Promise.all(FORGE_PARTS.map(loadPart));
}

/** Off, a merged part draws as its GLB's own meshes: hull-profile-check's --inject unmerged. */
export const PART_MERGE = { on: true };

// A vane's fold is found by name (custos.ts createVaneRig), so its outer panel
// and barrel merge apart from the rest and keep the name class.
function foldRole(o: THREE.Object3D, top: THREE.Object3D): '' | '-outer-' | '-fold-hinge' {
  for (let x: THREE.Object3D | null = o; x && x !== top; x = x.parent) {
    if (x.name.includes('-outer-')) return '-outer-';
    if (x.name.endsWith('-fold-hinge')) return '-fold-hinge';
  }
  return '';
}

function attrSignature(g: THREE.BufferGeometry): string {
  const attrs = Object.keys(g.attributes).sort().map((k) => {
    const a = g.getAttribute(k) as THREE.BufferAttribute;
    return `${k}:${a.itemSize}:${a.normalized ? 'n' : ''}:${a.array.constructor.name}`;
  });
  return `${g.index ? 'i' : 'n'}|${attrs.join(',')}|${Object.keys(g.morphAttributes).sort().join(',')}`;
}

function flipWinding(g: THREE.BufferGeometry): void {
  const idx = g.index;
  if (idx) {
    for (let i = 0; i + 2 < idx.count; i += 3) {
      const b = idx.getX(i + 1); idx.setX(i + 1, idx.getX(i + 2)); idx.setX(i + 2, b);
    }
    return;
  }
  for (const a of Object.values(g.attributes) as THREE.BufferAttribute[]) {
    for (let i = 0; i + 2 < a.count; i += 3) {
      for (let c = 0; c < a.itemSize; c++) {
        const t = a.getComponent(i + 1, c);
        a.setComponent(i + 1, c, a.getComponent(i + 2, c)); a.setComponent(i + 2, c, t);
      }
    }
  }
}

/**
 * One mesh per material name and attribute layout, baked into the meshes'
 * deepest common ancestor. The ancestors are kept as they were: the triplanar
 * samples object space, and a walk of local matrices must read the same frame.
 */
function mergePart(name: PartName, proto: THREE.Group): THREE.Group {
  proto.updateMatrixWorld(true);
  const meshes: THREE.Mesh[] = [];
  proto.traverse((o) => { if ((o as THREE.Mesh).isMesh) meshes.push(o as THREE.Mesh); });
  const chain = (o: THREE.Object3D): THREE.Object3D[] => {
    const c: THREE.Object3D[] = [];
    for (let x = o.parent; x; x = x.parent) c.unshift(x);
    return c;
  };
  let common = meshes[0] ? chain(meshes[0]) : [proto];
  for (const m of meshes) {
    const c = chain(m);
    let k = 0;
    while (k < common.length && common[k] === c[k]) k++;
    common = common.slice(0, k);
  }
  const top = common[common.length - 1] ?? proto;
  const identity = new THREE.Matrix4();
  const groups = new Map<string, { geoms: THREE.BufferGeometry[]; mat: THREE.Material; slot: string; role: string }>();
  for (const m of meshes) {
    const g = m.geometry.clone();
    const rel = new THREE.Matrix4();
    for (let x: THREE.Object3D | null = m; x && x !== top; x = x.parent) rel.premultiply(x.matrix);
    if (!rel.equals(identity)) g.applyMatrix4(rel);
    if (rel.determinant() < 0) flipWinding(g);
    const mat = m.material as THREE.Material;
    const slot = (mat.name || '').split('.')[0] ?? '';
    const role = foldRole(m, top);
    const key = `${role}|${slot}|${attrSignature(g)}`;
    const grp = groups.get(key);
    if (grp) grp.geoms.push(g);
    else groups.set(key, { geoms: [g], mat, slot, role });
  }
  const root = new THREE.Group();
  let frame: THREE.Object3D = root;
  for (const a of common) {
    const g = a === proto ? root : new THREE.Group();
    g.name = a.name;
    g.position.copy(a.position); g.quaternion.copy(a.quaternion); g.scale.copy(a.scale);
    if (g !== root) frame.add(g);
    frame = g;
  }
  for (const { geoms, mat, slot, role } of groups.values()) {
    const merged = geoms.length > 1 ? mergeGeometries(geoms) : geoms[0]!;
    for (const geom of merged ? [merged] : geoms) {
      const mesh = new THREE.Mesh(geom, mat);
      mesh.name = role === '-fold-hinge' ? `${name}:${slot}-fold-hinge` : `${name}${role || ':'}${slot}`;
      mesh.userData = { sharedGeometry: true, slot };
      frame.add(mesh);
    }
    if (merged && geoms.length > 1) for (const g of geoms) g.dispose();
  }
  return root;
}

function loadMerged(name: PartName): Promise<THREE.Group> {
  const key = `merged:${name}`;
  let p = _cache.get(key);
  if (!p) {
    p = loadPart(name).then((proto) => mergePart(name, proto));
    _cache.set(key, p);
  }
  return p;
}

/**
 * Place-a-part: returns a holder group SYNCHRONOUSLY (the builder stays sync);
 * the cloned part pops in when the GLB lands (instant once cached). Ship
 * materials are bound by the placeholder material's name. `merged` draws one
 * mesh per slot, for a whole hull or a vane rather than a part placed many times.
 */
export function instancePart(name: PartName, mats: PartMats, onLoad?: (inst: THREE.Object3D) => void,
                             merged = false): THREE.Group {
  const holder = new THREE.Group();
  holder.name = `part:${name}`;
  void (merged && PART_MERGE.on ? loadMerged(name) : loadPart(name)).then((proto) => {
    const inst = proto.clone(true); // shares geometry; userData flag carries over
    inst.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      const slot = ((mesh.material as THREE.Material).name || '').split('.')[0] ?? '';
      mesh.material = mats[PART_SLOTS[slot] ?? 'slate'] ?? mats.slate;
    });
    holder.add(inst);
    onLoad?.(inst);
  });
  return holder;
}
